/**
 * UnifiedScoringCalculator.ts - Simplified unified scoring system
 * 
 * This calculator implements a streamlined scoring formula:
 * Final Score = Base Score + Investment Score - Incident Penalty
 */

import { Investment } from '../types/Investment';
import { IncidentResponse, Incident } from '../types/Incident';
import { ScoringConfiguration } from '../types/ScoringConfiguration';
import { BusinessCriteria } from '../services/UnifiedScoringService';
import { SynergyMapping } from '../types/ScenarioManagement';
import { SynergyProcessor, ActiveSynergy } from './SynergyProcessor';
import { createLegacySynergyMappings, shouldUseLegacySynergies } from './legacySynergyMigration';

export interface ScoreComponents {
  security: number;
  reputation: number;
  financial: number;
  operational: number;
}

export interface SimplifiedScoreResult {
  baseScore: ScoreComponents;
  investmentScore: ScoreComponents;
  incidentPenalty: ScoreComponents;
  finalScore: ScoreComponents;
  overallScore: number;
}

export interface UnifiedScoreResult extends SimplifiedScoreResult {
  grade: string;
  performance: string;
  businessCriteria: BusinessCriteria;
  investmentImpactDetails?: any[];
  activeSynergies?: ActiveSynergy[];
}

/**
 * Simplified unified scoring calculator
 */
export class UnifiedScoringCalculator {
  private configuration: ScoringConfiguration;
  private synergyProcessor: SynergyProcessor;
  private scenarioSynergies: SynergyMapping[] = [];
  
  constructor(configuration: ScoringConfiguration) {
    this.configuration = configuration;
    // Initialize with legacy synergies if synergies are enabled but none provided
    if (shouldUseLegacySynergies(this.scenarioSynergies, configuration.enableSynergies)) {
      this.scenarioSynergies = createLegacySynergyMappings();
    }
    this.synergyProcessor = new SynergyProcessor(this.scenarioSynergies, configuration.enableSynergies);
  }
  
  /**
   * Update configuration
   */
  public setConfiguration(configuration: ScoringConfiguration): void {
    this.configuration = configuration;
    // Update synergy processor with new logging preference
    this.synergyProcessor = new SynergyProcessor(this.scenarioSynergies, configuration.enableSynergies);
  }
  
  /**
   * Update scenario synergies
   */
  public setScenarioSynergies(synergies: SynergyMapping[]): void {
    this.scenarioSynergies = synergies;
    this.synergyProcessor.updateSynergies(synergies);
  }
  
  /**
   * Main scoring calculation method - simplified formula
   */
  public calculateScore(
    investments: Investment[],
    incidentResponses: IncidentResponse[],
    incidents: Incident[],
    scenarioSynergies?: SynergyMapping[]
  ): UnifiedScoreResult {
    // Update synergies if provided, otherwise use legacy if needed
    if (scenarioSynergies) {
      this.setScenarioSynergies(scenarioSynergies);
    } else if (shouldUseLegacySynergies(this.scenarioSynergies, this.configuration.enableSynergies)) {
      this.setScenarioSynergies(createLegacySynergyMappings());
    }
    // Step 1: Base score (always 50 for each category)
    const baseScore: ScoreComponents = {
      security: 50,
      reputation: 50,
      financial: 50,
      operational: 50
    };
    
    // Step 2: Calculate investment bonuses
    const investmentScore = this.calculateInvestmentScore(investments);
    
    // Step 3: Calculate incident penalties
    const incidentPenalty = this.calculateIncidentPenalty(
      incidentResponses,
      incidents,
      investments
    );
    
    // Step 4: Calculate final score
    const finalScore = this.calculateFinalScore(
      baseScore,
      investmentScore,
      incidentPenalty
    );
    
    // Step 5: Calculate overall score
    const overallScore = this.calculateOverallScore(finalScore);
    
    // Calculate grade and performance
    const grade = this.calculateGrade(overallScore);
    const performance = this.getPerformanceDescription(overallScore);
    
    // Map to business criteria
    const businessCriteria = this.mapToBusinessCriteria(finalScore);
    
    // Get all active synergies
    const activeSynergies = this.synergyProcessor.getActiveSynergies(investments);
    
    return {
      baseScore,
      investmentScore,
      incidentPenalty,
      finalScore,
      overallScore,
      grade,
      performance,
      businessCriteria,
      activeSynergies: activeSynergies.length > 0 ? activeSynergies : undefined
    };
  }
  
  /**
   * Calculate investment score - simple flat bonuses
   */
  private calculateInvestmentScore(investments: Investment[]): ScoreComponents {
    const score: ScoreComponents = {
      security: 0,
      reputation: 0,
      financial: 0,
      operational: 0
    };
    
    investments.forEach(investment => {
      const effect = this.configuration.investmentEffects[investment.id];
      if (effect) {
        score.security += effect.categoryBonuses.security;
        score.reputation += effect.categoryBonuses.reputation;
        score.financial += effect.categoryBonuses.financial;
        score.operational += effect.categoryBonuses.operational;
      }
    });
    
    return score;
  }
  
  /**
   * Calculate incident penalty with investment-response synergies
   */
  private calculateIncidentPenalty(
    incidentResponses: IncidentResponse[],
    incidents: Incident[],
    investments: Investment[]
  ): ScoreComponents {
    const penalty: ScoreComponents = {
      security: 0,
      reputation: 0,
      financial: 0,
      operational: 0
    };
    
    // Calculate total mitigation from investments
    let totalMitigation = 0;
    investments.forEach(investment => {
      const effect = this.configuration.investmentEffects[investment.id];
      if (effect) {
        totalMitigation += effect.incidentMitigation;
      }
    });
    
    // Cap mitigation at 70%
    const effectiveMitigation = Math.min(totalMitigation, 0.7);
    
    // Apply penalties for each incident response
    incidentResponses.forEach(response => {
      const incident = incidents.find(i => i.id === response.incidentId);
      if (!incident) return;
      
      const option = incident.responseOptions.find(o => o.id === response.selectedOptionId);
      if (!option) return;
      
      // Calculate response-specific mitigation based on investment synergies
      let responseMitigation = effectiveMitigation;
      
      // Use SynergyProcessor to calculate synergies
      const synergies = this.synergyProcessor.calculateResponseSynergies(
        option,
        incident,
        investments
      );
      
      // Add synergy bonus to mitigation (capped at 85% total)
      responseMitigation = Math.min(responseMitigation + synergies.mitigationBonus, 0.85);
      
      // Apply response impact with mitigation
      // Positive impacts (benefits) are enhanced by synergies
      // Negative impacts (penalties) are reduced by mitigation
      penalty.security += this.applyResponseImpact(option.impact.security, responseMitigation, synergies.securityBonus);
      penalty.reputation += this.applyResponseImpact(option.impact.reputation, responseMitigation, synergies.reputationBonus);
      penalty.financial += this.applyResponseImpact(option.impact.financial, responseMitigation, synergies.financialBonus);
      penalty.operational += this.applyResponseImpact(option.impact.operational, responseMitigation, synergies.operationalBonus);
    });
    
    return penalty;
  }
  
  /**
   * Apply response impact considering mitigation and synergy bonuses
   */
  private applyResponseImpact(
    baseImpact: number,
    mitigation: number,
    synergyBonus: number
  ): number {
    if (baseImpact >= 0) {
      // Positive impact (benefit) - enhance with synergy
      return -(baseImpact * (1 + synergyBonus));
    } else {
      // Negative impact (penalty) - reduce with mitigation
      return Math.abs(baseImpact) * (1 - mitigation);
    }
  }
  
  
  /**
   * Calculate final score
   */
  private calculateFinalScore(
    baseScore: ScoreComponents,
    investmentScore: ScoreComponents,
    incidentPenalty: ScoreComponents
  ): ScoreComponents {
    // Apply the simple formula
    return {
      security: Math.max(0, Math.min(100, baseScore.security + investmentScore.security - incidentPenalty.security)),
      reputation: Math.max(0, Math.min(100, baseScore.reputation + investmentScore.reputation - incidentPenalty.reputation)),
      financial: Math.max(0, Math.min(100, baseScore.financial + investmentScore.financial - incidentPenalty.financial)),
      operational: Math.max(0, Math.min(100, baseScore.operational + investmentScore.operational - incidentPenalty.operational))
    };
  }
  
  /**
   * Calculate overall score using configuration weights
   */
  private calculateOverallScore(finalScore: ScoreComponents): number {
    const weights = this.configuration.categoryWeights;
    
    const weighted = 
      finalScore.security * weights.security +
      finalScore.reputation * weights.reputation +
      finalScore.financial * weights.financial +
      finalScore.operational * weights.operational;
    
    return Math.round(Math.max(0, Math.min(100, weighted)));
  }
  
  /**
   * Calculate incident preview with synergies
   */
  public calculateIncidentPreview(
    optionId: string,
    incident: Incident,
    investments: Investment[],
    scenarioSynergies?: SynergyMapping[]
  ): Partial<UnifiedScoreResult> & { synergies?: any } {
    // Update synergies if provided
    if (scenarioSynergies) {
      this.setScenarioSynergies(scenarioSynergies);
    }
    
    const option = incident.responseOptions.find(o => o.id === optionId);
    if (!option) return {};
    
    // Calculate base mitigation
    let totalMitigation = 0;
    investments.forEach(investment => {
      const effect = this.configuration.investmentEffects[investment.id];
      if (effect) {
        totalMitigation += effect.incidentMitigation;
      }
    });
    
    const effectiveMitigation = Math.min(totalMitigation, 0.7);
    
    // Use SynergyProcessor to calculate synergies
    const synergies = this.synergyProcessor.calculateResponseSynergies(option, incident, investments);
    
    // Apply synergy-enhanced mitigation
    const responseMitigation = Math.min(effectiveMitigation + synergies.mitigationBonus, 0.85);
    
    const incidentPenalty: ScoreComponents = {
      security: this.applyResponseImpact(option.impact.security, responseMitigation, synergies.securityBonus),
      reputation: this.applyResponseImpact(option.impact.reputation, responseMitigation, synergies.reputationBonus),
      financial: this.applyResponseImpact(option.impact.financial, responseMitigation, synergies.financialBonus),
      operational: this.applyResponseImpact(option.impact.operational, responseMitigation, synergies.operationalBonus)
    };
    
    return {
      incidentPenalty,
      synergies: {
        active: synergies.activeSynergies.length > 0,
        details: synergies,
        activeSynergies: synergies.activeSynergies
      }
    };
  }
  
  /**
   * Calculate grade based on score
   */
  private calculateGrade(score: number): string {
    const thresholds = this.configuration.gradeThresholds;
    const sortedThresholds = [...thresholds].sort((a, b) => b.minScore - a.minScore);
    
    for (const threshold of sortedThresholds) {
      if (score >= threshold.minScore) {
        return threshold.grade;
      }
    }
    
    return 'F';
  }
  
  /**
   * Get performance description based on score
   */
  private getPerformanceDescription(score: number): string {
    const thresholds = this.configuration.gradeThresholds;
    const sortedThresholds = [...thresholds].sort((a, b) => b.minScore - a.minScore);
    
    for (const threshold of sortedThresholds) {
      if (score >= threshold.minScore) {
        return threshold.label || threshold.description || 'Performance rating';
      }
    }
    
    return 'Needs improvement';
  }
  
  /**
   * Map score components to business criteria
   */
  private mapToBusinessCriteria(scores: ScoreComponents): BusinessCriteria {
    const safeScore = (score: number): number => {
      if (typeof score !== 'number' || isNaN(score) || !isFinite(score)) return 0;
      return Math.min(100, Math.max(0, score));
    };
    
    const security = safeScore(scores.security);
    const operational = safeScore(scores.operational);
    const reputation = safeScore(scores.reputation);
    const financial = safeScore(scores.financial);
    
    // Simple mapping
    return {
      regulatoryCompliance: Math.round((security + operational) / 2),
      operationalResiliency: Math.round((operational + financial) / 2),
      customerTrust: Math.round((reputation + security) / 2),
      ipProtection: Math.round((security * 0.7) + (operational * 0.3)),
      productIntegrity: Math.round((security + operational + reputation + financial) / 4)
    };
  }
  
  /**
   * Get detailed scoring explanation
   */
  public getScoreExplanation(result: UnifiedScoreResult): string {
    let explanation = `Scoring Calculation:\n\n`;
    
    explanation += `Base Scores: 50 each\n\n`;
    
    explanation += `Investment Bonuses:\n`;
    explanation += `- Security: +${result.investmentScore.security}\n`;
    explanation += `- Reputation: +${result.investmentScore.reputation}\n`;
    explanation += `- Financial: +${result.investmentScore.financial}\n`;
    explanation += `- Operational: +${result.investmentScore.operational}\n\n`;
    
    explanation += `Incident Response Impact:\n`;
    explanation += `- Security: ${result.incidentPenalty.security >= 0 ? '-' : '+'}${Math.abs(Math.round(result.incidentPenalty.security))}\n`;
    explanation += `- Reputation: ${result.incidentPenalty.reputation >= 0 ? '-' : '+'}${Math.abs(Math.round(result.incidentPenalty.reputation))}\n`;
    explanation += `- Financial: ${result.incidentPenalty.financial >= 0 ? '-' : '+'}${Math.abs(Math.round(result.incidentPenalty.financial))}\n`;
    explanation += `- Operational: ${result.incidentPenalty.operational >= 0 ? '-' : '+'}${Math.abs(Math.round(result.incidentPenalty.operational))}\n\n`;
    
    // Include synergy information if available
    if (result.activeSynergies && result.activeSynergies.length > 0) {
      explanation += `Active Synergies:\n`;
      result.activeSynergies.forEach(synergy => {
        explanation += `- ${synergy.name}: ${synergy.description}\n`;
      });
      explanation += '\n';
    } else if (this.configuration.enableSynergies) {
      explanation += `Note: Investment-response synergies enabled but none activated\n\n`;
    }
    
    explanation += `Final Score: ${result.overallScore}/100 (${result.grade})\n`;
    explanation += `Performance: ${result.performance}`;
    
    return explanation;
  }
}

// Singleton instance for backward compatibility
let defaultCalculator: UnifiedScoringCalculator | null = null;

export function getDefaultCalculator(
  configuration: ScoringConfiguration
): UnifiedScoringCalculator {
  if (!defaultCalculator) {
    defaultCalculator = new UnifiedScoringCalculator(configuration);
  }
  return defaultCalculator;
}